import type { RootState } from '@store/rootReducer';
import { useSelector } from 'react-redux';

/**
 * Internal-user role helpers for the Control Center.
 *
 * Three internal roles:
 *   - SUPER_ADMIN: assignment + oversight (assign/reassign HRs, user management, dashboards)
 *   - HR:          assigned-account work only (their clients, pipelines, interviews)
 *   - MARKETING:   Guides, and nothing else
 *
 * **This used to be an allowlist of one, inverted.** `isSuperAdmin` was `!isHrRole`, so anyone
 * whose role was not literally "HR" was a Super Admin — including a role nobody had thought about
 * yet. Adding MARKETING under that model would have granted it every route, every gated API and
 * the agreement bucket, silently. So the polarity is reversed: privilege is granted by naming a
 * role, never by failing to match one.
 *
 * The cost of reversing it is that an ABSENT role no longer reads as privileged, and the old
 * behaviour was load-bearing while the profile loads. Guards must therefore gate on the profile
 * having actually arrived (`state.loginUserProfile.status`), not on the role alone — see
 * `RequireRoles`.
 *
 * Source of truth for the current user's role is the login profile record
 * (`state.loginUserProfile.profile.role`), which carries the real internal role.
 */
export const INTERNAL_HR_ROLE = 'HR';
export const SUPER_ADMIN_ROLE = 'SUPER_ADMIN';
export const MARKETING_ROLE = 'MARKETING';

/**
 * Values that mean Super Admin but are not the canonical enum.
 *
 * `admin` / `ADMIN` were written by hand on the earliest accounts and still sign in as Super Admin
 * (the BFF logs each such sign-in so they can be migrated to SUPER_ADMIN). Mirrors
 * backend/src/helpers/roles.ts.
 *
 * VIEWER is no longer here (2026-10-31). It was the users route's silent create-default, the role an
 * account got when nobody chose one, and it read as Super Admin by accident. The BFF now refuses a
 * VIEWER account at sign-in, as it does one with no role, so in the staff list it shows as a role
 * that grants nothing, which is what a Super Admin needs to see to fix it.
 */
const LEGACY_SUPER_ADMIN_ROLES = ['admin', 'ADMIN'];

/**
 * Every role a user may be assigned, with the copy the admin UI shows for each.
 *
 * The options and the description were hard-coded in three places — the create popup, the
 * workspace, and a binary ternary that described anything not SUPER_ADMIN as an HR. A third role
 * would have been offered in one list, missing from another, and described as HR in the third.
 */
export const ASSIGNABLE_ROLES = [SUPER_ADMIN_ROLE, INTERNAL_HR_ROLE, MARKETING_ROLE] as const;
export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

export const ROLE_OPTIONS: { value: AssignableRole; label: string }[] = [
  { value: SUPER_ADMIN_ROLE, label: 'Super Admin' },
  { value: INTERNAL_HR_ROLE, label: 'HR (Dedicated Recruiter)' },
  { value: MARKETING_ROLE, label: 'Marketing (view only for jobs and companies)' },
];

/** What a role may do, in the admin UI's words. */
export const describeRole = (role?: string | null): string => {
  if (isMarketingRole(role)) {
    return 'Sees what an HR sees and publishes guides. Cannot create, edit or assign jobs, or create or edit companies.';
  }
  if (isHrRole(role)) return 'Assigned accounts only: their own clients, pipelines and interviews.';
  if (isSuperAdmin(role)) {
    return 'Assignment and oversight: assign or reassign HRs, manage users, and see every dashboard.';
  }
  return 'No access: this role is not recognised by the Control Center.';
};

/** Hook: current internal user's role, or undefined while the profile is loading. */
export const useCurrentUserRole = (): string | undefined =>
  useSelector((state: RootState) => state.loginUserProfile.profile?.role);

/**
 * Hook: current internal user's id (`/api/me` returns it as `_id`), or undefined while the
 * profile is loading. Used to pin an HR's views to their own worklist.
 */
export const useCurrentUserId = (): string | undefined =>
  useSelector((state: RootState) => state.loginUserProfile.profile?._id);

/** A stored value that grants Super Admin without being the canonical enum; due for migration. */
export const isLegacyRole = (role?: string | null): boolean =>
  !!role && LEGACY_SUPER_ADMIN_ROLES.includes(role);

/** True when the caller is a dedicated HR. */
export const isHrRole = (role?: string | null): boolean => role === INTERNAL_HR_ROLE;

/** True when the caller is Marketing: HR's view of the console, read-only on jobs and companies, plus Guides. */
export const isMarketingRole = (role?: string | null): boolean => role === MARKETING_ROLE;

/**
 * Named, not inferred.
 *
 * Previously `!isHrRole`, which made every unrecognised value privileged. An unknown role now has
 * no access at all, which is the whole point of the change.
 */
export const isSuperAdmin = (role?: string | null): boolean =>
  role === SUPER_ADMIN_ROLE || (!!role && LEGACY_SUPER_ADMIN_ROLES.includes(role));

/** Who may access HR assignment / oversight areas: Super Admins only. */
export const canManageHrAssignments = (role?: string | null): boolean => isSuperAdmin(role);

/** Guides are the one surface Marketing shares with Super Admins. */
export const canAccessGuides = (role?: string | null): boolean =>
  isSuperAdmin(role) || isMarketingRole(role);

/** External jobs (employer career-site listings): every staff role. The BFF gate is the same. */
export const canAccessExternalJobs = (role?: string | null): boolean =>
  isSuperAdmin(role) || isHrRole(role) || isMarketingRole(role);

/** Any role the console recognises. An unknown role sees nothing. */
export const isKnownStaffRole = (role?: string | null): boolean =>
  isSuperAdmin(role) || isHrRole(role) || isMarketingRole(role);

/**
 * Who may create, edit or assign jobs, and create or edit companies and employer accounts.
 *
 * Everyone but Marketing (owner's brief, 2026-10-19): the marketing team runs sign-ups and
 * promotions and needs to see every job and company, but not to change the supply side. The BFF
 * (MARKETING_REFUSED_FIELDS) and the backend refuse the same operations, so hiding the controls
 * is presentation; the refusal does not depend on it.
 */
export const canWriteJobsAndCompanies = (role?: string | null): boolean =>
  isSuperAdmin(role) || isHrRole(role);

/** Hook form of canWriteJobsAndCompanies, for the pages that hide their edit controls. */
export const useCanWriteJobsAndCompanies = (): boolean =>
  canWriteJobsAndCompanies(useCurrentUserRole());

/**
 * Where a role lands when it has no business on the page it asked for.
 *
 * Both guards used to redirect to `/dashboard`, which Marketing must not see — so a Marketing user
 * would have been bounced from the dashboard to the dashboard forever.
 */
export const homeRouteFor = (_role?: string | null): string => '/dashboard';

/**
 * Where a role starts the day: the page after sign-in, and what `/` opens.
 *
 * Not `homeRouteFor`, which is where a guard bounces someone from a page they may not see. An HR
 * starts on their managed clients because that list is their work; bouncing them there from a
 * refused admin page as well would move them further from where they were than the dashboard does.
 */
export const landingRouteFor = (role?: string | null): string =>
  isHrRole(role) ? '/my-accounts' : homeRouteFor(role);

/**
 * The page to open once signed in.
 *
 * A deep link the user was sent to /login from wins, so a shared URL still lands where it points.
 * `/` and `/login` are not destinations: `/` is what an unauthenticated visit to the bare domain
 * became, and treating it as a choice would send every HR to the dashboard.
 */
export const postSignInRoute = (role?: string | null, from?: string | null): string => {
  const path = (from ?? '').split(/[?#]/)[0];
  if (!from || path === '/' || path === '/login' || !from.startsWith('/') || from.startsWith('//')) {
    return landingRouteFor(role);
  }
  return from;
};

/**
 * The stored enum as a readable label: SUPER_ADMIN -> "Super Admin".
 *
 * Presentation only — the value itself is never rewritten. Lives here rather
 * than in a component because the sidebar and the phone drawer both show it,
 * and two copies would eventually disagree.
 */
export const formatRole = (role?: string | null): string => {
  if (!role) return 'Not set';
  if (role === INTERNAL_HR_ROLE) return 'HR';
  // The legacy values grant Super Admin (see LEGACY_SUPER_ADMIN_ROLES). Title-casing them showed
  // "Admin" and "Viewer", and "Viewer" reads as read-only for an account that can do everything.
  if (isLegacyRole(role)) return 'Super Admin (legacy)';
  return role
    .replace(/_/g, ' ')
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};
